import React, { useEffect, useMemo } from 'react';
import type { ReminderMethod, ReminderOffset, ReminderOffsetKind, TaskPriority, TaskReminderProfile } from '../../../types/taskReminders';

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
const METHODS: Array<{ value: ReminderMethod; label: string }> = [
  { value: 'in_app', label: 'In-app' },
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push' },
  { value: 'sms', label: 'SMS' },
];

const OFFSET_KINDS: Array<{ value: ReminderOffsetKind; label: string }> = [
  { value: 'before_due', label: 'Before due' },
  { value: 'at_due', label: 'At due' },
  { value: 'after_due', label: 'After due' },
];

const DEFAULT_REMINDER_PROFILE: TaskReminderProfile = {
  includeAssignee: true,
  includeWatchers: false,
  methodsByPriority: {
    low: ['in_app'],
    medium: ['in_app', 'email'],
    high: ['in_app', 'email'],
    urgent: ['in_app', 'email'],
  },
  offsetsByPriority: {
    low: [
      { kind: 'before_due', minutes: 24 * 60, timeOfDay: '08:00' },
    ],
    medium: [
      { kind: 'before_due', minutes: 3 * 24 * 60, timeOfDay: '08:00' },
      { kind: 'at_due', timeOfDay: '08:00' },
    ],
    high: [
      { kind: 'before_due', minutes: 7 * 24 * 60, timeOfDay: '08:00' },
      { kind: 'before_due', minutes: 3 * 24 * 60, timeOfDay: '08:00' },
      { kind: 'before_due', minutes: 24 * 60, timeOfDay: '08:00' },
      { kind: 'at_due', timeOfDay: '08:00' },
    ],
    urgent: [
      { kind: 'before_due', minutes: 7 * 24 * 60, timeOfDay: '08:00' },
      { kind: 'before_due', minutes: 3 * 24 * 60, timeOfDay: '08:00' },
      { kind: 'before_due', minutes: 24 * 60, timeOfDay: '08:00' },
      { kind: 'at_due', timeOfDay: '08:00' },
      { kind: 'before_due', minutes: 120 },
    ],
  },
};

const cloneProfile = (profile: TaskReminderProfile): TaskReminderProfile => JSON.parse(JSON.stringify(profile));

interface ReminderProfileEditorProps {
  value: TaskReminderProfile | null | undefined;
  onChange: (next: TaskReminderProfile | null) => void;
  title?: string;
  description?: string;
  allowClear?: boolean;
  showTimezone?: boolean;
  fallbackTimezone?: string;
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
}

export const ReminderProfileEditor: React.FC<ReminderProfileEditorProps> = ({
  value,
  onChange,
  title = 'Task Reminder Profile',
  description,
  allowClear = false,
  showTimezone = false,
  fallbackTimezone,
  onValidationChange,
}) => {
  const isCustom = value != null;
  const profile = isCustom ? value! : DEFAULT_REMINDER_PROFILE;
  const disabled = allowClear && !isCustom;

  const updateProfile = (next: TaskReminderProfile) => {
    onChange(next);
  };

  const ensureMethods = (input?: TaskReminderProfile['methodsByPriority']) => input || {};
  const ensureOffsets = (input?: TaskReminderProfile['offsetsByPriority']) => input || {};

  const handleMethodToggle = (priority: TaskPriority, method: ReminderMethod) => {
    const currentMethods = ensureMethods(profile.methodsByPriority)[priority] || [];
    const hasMethod = currentMethods.includes(method);
    const nextMethods = hasMethod
      ? currentMethods.filter(item => item !== method)
      : [...currentMethods, method];

    updateProfile({
      ...profile,
      methodsByPriority: {
        ...ensureMethods(profile.methodsByPriority),
        [priority]: nextMethods,
      },
    });
  };

  const handleOffsetChange = (priority: TaskPriority, index: number, next: ReminderOffset) => {
    const currentOffsets = ensureOffsets(profile.offsetsByPriority)[priority] || [];
    const updatedOffsets = currentOffsets.map((offset, idx) => (idx === index ? next : offset));

    updateProfile({
      ...profile,
      offsetsByPriority: {
        ...ensureOffsets(profile.offsetsByPriority),
        [priority]: updatedOffsets,
      },
    });
  };

  const handleOffsetAdd = (priority: TaskPriority) => {
    const currentOffsets = ensureOffsets(profile.offsetsByPriority)[priority] || [];
    const updatedOffsets = [
      ...currentOffsets,
      { kind: 'before_due', minutes: 60, timeOfDay: '08:00' },
    ];

    updateProfile({
      ...profile,
      offsetsByPriority: {
        ...ensureOffsets(profile.offsetsByPriority),
        [priority]: updatedOffsets,
      },
    });
  };

  const handleOffsetRemove = (priority: TaskPriority, index: number) => {
    const currentOffsets = ensureOffsets(profile.offsetsByPriority)[priority] || [];
    const updatedOffsets = currentOffsets.filter((_, idx) => idx !== index);

    updateProfile({
      ...profile,
      offsetsByPriority: {
        ...ensureOffsets(profile.offsetsByPriority),
        [priority]: updatedOffsets,
      },
    });
  };

  const handleTimezoneChange = (nextTimezone: string) => {
    updateProfile({
      ...profile,
      timezone: nextTimezone || undefined,
    });
  };

  const handleBooleanToggle = (field: 'includeAssignee' | 'includeWatchers', nextValue: boolean) => {
    updateProfile({
      ...profile,
      [field]: nextValue,
    });
  };

  const handleResetDefaults = () => {
    updateProfile(cloneProfile(DEFAULT_REMINDER_PROFILE));
  };

  const handleClearProfile = () => {
    onChange(null);
  };

  const handleEnableCustom = () => {
    updateProfile(cloneProfile(value ?? DEFAULT_REMINDER_PROFILE));
  };

  const validationErrors = useMemo(() => {
    if (disabled) return [];
    const errors: string[] = [];
    const effectiveMethods = ensureMethods(profile.methodsByPriority);
    const effectiveOffsets = ensureOffsets(profile.offsetsByPriority);

    if (!profile.includeAssignee && !profile.includeWatchers) {
      errors.push('At least one recipient type (assignee or watchers) must be selected.');
    }

    for (const priority of PRIORITIES) {
      const methods = effectiveMethods[priority] || [];
      const offsets = effectiveOffsets[priority] || [];
      if (methods.length === 0) {
        errors.push(`${priority.toUpperCase()} priority must include at least one delivery method.`);
      }
      if (offsets.length === 0) {
        errors.push(`${priority.toUpperCase()} priority must include at least one reminder offset.`);
      }

      offsets.forEach((offset, index) => {
        if ((offset.kind === 'before_due' || offset.kind === 'after_due') && (offset.minutes == null || offset.minutes <= 0)) {
          errors.push(`${priority.toUpperCase()} offset #${index + 1} must include positive minutes.`);
        }
        if (offset.timeOfDay) {
          const match = offset.timeOfDay.match(/^(\d{2}):(\d{2})$/);
          if (!match) {
            errors.push(`${priority.toUpperCase()} offset #${index + 1} has invalid time (use HH:mm).`);
          } else {
            const hour = Number(match[1]);
            const minute = Number(match[2]);
            if (hour > 23 || minute > 59) {
              errors.push(`${priority.toUpperCase()} offset #${index + 1} has invalid time (HH:mm).`);
            }
          }
        }
      });
    }

    return errors;
  }, [disabled, profile]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validationErrors.length === 0, validationErrors);
    }
  }, [onValidationChange, validationErrors]);

  const previewTimezone = profile.timezone || fallbackTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const sampleDueDate = useMemo(() => {
    const now = new Date();
    const due = new Date(now);
    due.setDate(now.getDate() + 7);
    due.setHours(17, 0, 0, 0);
    return due;
  }, []);

  const previewItems = useMemo(() => {
    const items: Array<{ priority: TaskPriority; label: string; date: Date }> = [];
    const effectiveOffsets = ensureOffsets(profile.offsetsByPriority);
    for (const priority of PRIORITIES) {
      const offsets = effectiveOffsets[priority] || [];
      offsets.forEach((offset, index) => {
        const base = new Date(sampleDueDate);
        if (offset.timeOfDay) {
          const match = offset.timeOfDay.match(/^(\d{2}):(\d{2})$/);
          if (match) {
            base.setHours(Number(match[1]), Number(match[2]), 0, 0);
          }
        }

        let scheduled = new Date(base);
        if (offset.kind === 'before_due' && typeof offset.minutes === 'number') {
          scheduled = new Date(base.getTime() - offset.minutes * 60000);
        }
        if (offset.kind === 'after_due' && typeof offset.minutes === 'number') {
          scheduled = new Date(base.getTime() + offset.minutes * 60000);
        }

        items.push({
          priority,
          label: `#${index + 1} ${offset.kind.replace('_', ' ')}`,
          date: scheduled,
        });
      });
    }
    return items;
  }, [profile.offsetsByPriority, sampleDueDate]);

  const formatPreview = (date: Date) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: previewTimezone,
      }).format(date);
    } catch {
      return date.toLocaleString();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h4>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {allowClear && (
              <button
                type="button"
                onClick={isCustom ? handleClearProfile : handleEnableCustom}
                className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {isCustom ? 'Use Firm Defaults' : 'Override Defaults'}
              </button>
            )}
            <button
              type="button"
              onClick={handleResetDefaults}
              disabled={disabled}
              className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Reset to Defaults
            </button>
          </div>
        </div>
        {allowClear && !isCustom && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            This user is currently using firm-level reminder defaults.
          </div>
        )}
        {validationErrors.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-700/60 dark:bg-red-900/20 dark:text-red-200">
            <div className="font-semibold mb-1">Reminder profile needs attention:</div>
            <ul className="list-disc list-inside space-y-1">
              {validationErrors.slice(0, 6).map((error, idx) => (
                <li key={`${error}-${idx}`}>{error}</li>
              ))}
              {validationErrors.length > 6 && (
                <li>And {validationErrors.length - 6} more issues.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className={`space-y-4 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={Boolean(profile.includeAssignee)}
              onChange={(event) => handleBooleanToggle('includeAssignee', event.target.checked)}
              className="h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded focus:ring-lex-accent"
            />
            Include assignee
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={Boolean(profile.includeWatchers)}
              onChange={(event) => handleBooleanToggle('includeWatchers', event.target.checked)}
              className="h-4 w-4 text-lex-accent border-gray-300 dark:border-gray-600 rounded focus:ring-lex-accent"
            />
            Include watchers
          </label>
        </div>

        {showTimezone && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Timezone override (IANA)
            </label>
            <input
              type="text"
              value={profile.timezone || ''}
              onChange={(event) => handleTimezoneChange(event.target.value)}
              placeholder="America/New_York"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        )}

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">Preview (sample due date)</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Uses sample due date {formatPreview(sampleDueDate)} in {previewTimezone}. Actual scheduling uses server-side timezone resolution.
          </div>
          <div className="space-y-2">
            {PRIORITIES.map(priority => {
              const priorityItems = previewItems.filter(item => item.priority === priority).slice(0, 3);
              return (
                <div key={`preview-${priority}`} className="text-xs text-gray-600 dark:text-gray-300">
                  <div className="font-medium capitalize">{priority} priority</div>
                  {priorityItems.length === 0 ? (
                    <div className="text-gray-400">No offsets configured.</div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {priorityItems.map((item, idx) => (
                        <span
                          key={`${priority}-${idx}-${item.label}`}
                          className="px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300"
                        >
                          {item.label}: {formatPreview(item.date)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {PRIORITIES.map(priority => (
          <div key={priority} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 capitalize">
                {priority} priority
              </div>
              <button
                type="button"
                onClick={() => handleOffsetAdd(priority)}
                className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Add offset
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {METHODS.map(method => {
                const active = (ensureMethods(profile.methodsByPriority)[priority] || []).includes(method.value);
                return (
                  <label key={method.value} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => handleMethodToggle(priority, method.value)}
                      className="h-3 w-3 text-lex-accent border-gray-300 dark:border-gray-600 rounded focus:ring-lex-accent"
                    />
                    {method.label}
                  </label>
                );
              })}
            </div>

            <div className="space-y-2">
              {(ensureOffsets(profile.offsetsByPriority)[priority] || []).length === 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  No reminder offsets configured for this priority.
                </div>
              )}

              {(ensureOffsets(profile.offsetsByPriority)[priority] || []).map((offset, index) => (
                <div key={`${priority}-${index}`} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center">
                  <select
                    value={offset.kind}
                    onChange={(event) => {
                      const nextKind = event.target.value as ReminderOffsetKind;
                      handleOffsetChange(priority, index, {
                        ...offset,
                        kind: nextKind,
                        minutes: nextKind === 'at_due' ? undefined : offset.minutes,
                      });
                    }}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {OFFSET_KINDS.map(kind => (
                      <option key={kind.value} value={kind.value}>{kind.label}</option>
                    ))}
                  </select>

                  <input
                    type="number"
                    min={0}
                    value={offset.minutes ?? ''}
                    onChange={(event) => {
                      const nextMinutes = event.target.value === '' ? undefined : Number(event.target.value);
                      handleOffsetChange(priority, index, {
                        ...offset,
                        minutes: Number.isFinite(nextMinutes) ? nextMinutes : undefined,
                      });
                    }}
                    placeholder="Minutes"
                    disabled={offset.kind === 'at_due'}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60"
                  />

                  <input
                    type="time"
                    value={offset.timeOfDay || ''}
                    onChange={(event) => handleOffsetChange(priority, index, {
                      ...offset,
                      timeOfDay: event.target.value || undefined,
                    })}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />

                  <div className="text-xs text-gray-500 dark:text-gray-400 md:col-span-1">
                    {offset.kind === 'before_due' && 'Before due'}
                    {offset.kind === 'at_due' && 'At due'}
                    {offset.kind === 'after_due' && 'After due'}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleOffsetRemove(priority, index)}
                    className="text-xs text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
